"use client"

import { memo } from "react"
import { TableRow, TableCell } from "@/components/ui/table"
import FilterInput, { Filter } from "@/components/ui/filter-input"
import { TableColumnConfig, ColumnType } from "@/types/table"

export interface FilterState {
  [key: string]: Filter
}

export type CustomFilterHandler<T = any> = (item: T, filter: Filter) => boolean

export interface FilterConfig<T = any> {
  columnTypes: Record<string, ColumnType>
  customHandlers?: Record<string, CustomFilterHandler<T>>
}

// Helper function to check if a value matches the filter
export const matchesFilter = (
  value: any, 
  filter: Filter, 
  columnType: string = 'string',
  customHandler?: CustomFilterHandler
): boolean => {
  if (!filter.value && filter.operator !== 'between') return true
  if (value === null || value === undefined) return false

  // Use custom handler if provided
  if (customHandler) {
    return customHandler(value, filter)
  }

  const compareValue = (val: any, type: string): any => {
    if (val === null || val === undefined) return null
    
    switch (type) {
      case 'date': {
        // Always convert to YYYY-MM-DD format for consistent comparison
        const dateStr = typeof val === 'string' ? val : new Date(val).toISOString()
        return dateStr.split('T')[0]
      }
      case 'number':
      case 'percentage':
      case 'currency':
        return typeof val === 'number' ? val : parseFloat(val) || 0
      default:
        return String(val).toLowerCase()
    }
  }

  const filterValue = compareValue(filter.value, columnType)
  const actualValue = compareValue(value, columnType)

  switch (filter.operator) {
    case 'contains':
      return String(actualValue).toLowerCase().includes(String(filterValue).toLowerCase())
    case '=':
      // Handle comma-separated values for multi-select
      if (typeof filter.value === 'string' && filter.value.includes(',')) {
        const filterValues = filter.value.split(',').map(val => compareValue(val, columnType))
        return filterValues.some(val => val === actualValue)
      }
      return actualValue === filterValue
    case '!=':
      return actualValue !== filterValue
    case '<':
      return actualValue < filterValue
    case '>':
      return actualValue > filterValue
    case '<=':
      return actualValue <= filterValue
    case '>=':
      return actualValue >= filterValue
    case 'between':
      const filterValue2 = compareValue(filter.value2, columnType)
      return actualValue >= filterValue && actualValue <= filterValue2
    default:
      return true
  }
}

// Helper function to apply all filters to data
export const applyFilters = <T extends Record<string, any>>(
  data: T[], 
  filters: FilterState,
  config: FilterConfig<T>
): T[] => {
  return data.filter(item => {
    return Object.entries(filters).every(([column, filter]) => {
      const customHandler = config.customHandlers?.[column]
      // If custom handler exists, pass the entire item instead of just the column value
      if (customHandler) {
        return customHandler(item, filter)
      }
      return matchesFilter(item[column], filter, config.columnTypes[column])
    })
  })
}

// Helper function to handle filter changes
export const handleFilterChange = (
  column: string, 
  filter: Filter | undefined,
  setFilters: (filters: FilterState | ((prev: FilterState) => FilterState)) => void,
  onPageChange?: (page: number) => void
) => {
  setFilters((prevFilters: FilterState) => {
    if (!filter) {
      const { [column]: _, ...rest } = prevFilters
      return rest
    }
    return { ...prevFilters, [column]: filter }
  })
  // Reset to first page when filter changes
  onPageChange?.(1)
}

// Helper function to clear all filters
export const handleClearFilters = (
  setFilters: (filters: FilterState) => void,
  onPageChange?: (page: number) => void
) => {
  setFilters({})
  // Reset to first page when clearing filters
  onPageChange?.(1)
}

export interface FilterRowProps<T> {
  columns: TableColumnConfig<T>[]
  filters: FilterState
  onFilterChange: (column: string, filter: Filter | undefined) => void
  dictionary?: any
  data: T[]
  showCheckbox?: boolean
}

export function FilterRow<T>({
  columns,
  filters,
  onFilterChange,
  dictionary,
  data,
  showCheckbox = false
}: FilterRowProps<T>) {
  return (
    <TableRow className="sticky top-[48px] bg-white !border-0 z-10">
      {showCheckbox && (
        <TableCell className={`w-[50px] p-0 bg-white border-b-[3px] border-b-[#dddddd] border-r-[1px] border-r-[#dddddd] relative`} />
      )}
      {columns.map((column) => {
        if (!column.filterable) {
          return <TableCell key={String(column.key)} className={`p-0 bg-white border-b-[3px] border-b-[#dddddd] border-r-[1px] border-r-[#dddddd] relative`} />
        }

        const selectOptions = column.getFilterOptions ? column.getFilterOptions(data || []) : []

        return (
          <TableCell key={String(column.key)} className={`p-0 bg-white border-b-[3px] border-b-[#dddddd] border-r-[1px] border-r-[#dddddd] relative`}>
            <FilterInput 
              column={String(column.key)}
              columnType={column.type}
              filter={filters[String(column.key)]}
              onFilterChange={onFilterChange}
              dictionary={dictionary}
              isSelectInput={selectOptions.length > 0}
              selectOptions={selectOptions}
            />
          </TableCell>
        )
      })}
    </TableRow>
  )
}

const MemoizedFilterRow = memo(FilterRow) as <T>(props: FilterRowProps<T>) => JSX.Element
export default MemoizedFilterRow 